const __DEV__ = process.env.NODE_ENV !== "production";

const globalState = (() => {
  if (typeof window === "undefined") {
    return {
      renders: new Map(),
      traces: [],
      previousProps: new Map(),
      previousStates: new Map(),
      sequence: 0,
    };
  }

  if (!window.__GPS_RENDER_PROFILER__) {
    window.__GPS_RENDER_PROFILER__ = {
      renders: new Map(),
      traces: [],
      previousProps: new Map(),
      previousStates: new Map(),
      sequence: 0,
    };
  }

  return window.__GPS_RENDER_PROFILER__;
})();

const safeNow = () => {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
};

const shallowDiff = (prev = {}, next = {}) => {
  const changed = [];
  const keys = new Set([
    ...Object.keys(prev || {}),
    ...Object.keys(next || {}),
  ]);

  keys.forEach((key) => {
    const before = prev?.[key];
    const after = next?.[key];
    if (!Object.is(before, after)) {
      changed.push({
        key,
        before: summarizeValue(before),
        after: summarizeValue(after),
      });
    }
  });

  return changed;
};

const summarizeValue = (value) => {
  if (Array.isArray(value)) {
    return `array(len=${value.length})`;
  }
  if (value && typeof value === "object") {
    if (value instanceof Date) return value.toISOString();
    const keys = Object.keys(value);
    return `object(keys=${keys.slice(0, 6).join(",")}${keys.length > 6 ? ",…" : ""})`;
  }
  if (typeof value === "function") return "function";
  return value;
};

export const markRenderTrigger = (source, details = {}) => {
  if (!__DEV__) return;
  globalState.sequence += 1;
  globalState.traces.push({
    seq: globalState.sequence,
    ts: Date.now(),
    source,
    details,
  });
  if (globalState.traces.length > 200) {
    globalState.traces.splice(0, globalState.traces.length - 200);
  }
};

export const beginProfile = (componentName, meta = {}) => {
  if (!__DEV__) return () => {};
  const startedAt = safeNow();
  const previousProps = globalState.previousProps.get(componentName) || {};
  const previousState = globalState.previousStates.get(componentName) || {};
  return (nextSnapshot = {}) => {
    const durationMs = Number((safeNow() - startedAt).toFixed(2));
    const renderCount = (globalState.renders.get(componentName) || 0) + 1;
    globalState.renders.set(componentName, renderCount);

    const changedProps = shallowDiff(previousProps, nextSnapshot.props || {});
    const changedState = shallowDiff(previousState, nextSnapshot.state || {});
    const trigger = globalState.traces[globalState.traces.length - 1] || null;

    const payload = {
      component: componentName,
      renderCount,
      durationMs,
      deviceCount:
        nextSnapshot.deviceCount ??
        nextSnapshot.props?.deviceCount ??
        nextSnapshot.state?.deviceCount ??
        null,
      changedProps,
      changedState,
      triggerSource: trigger?.source || meta.triggerSource || "render",
      triggerDetails: trigger?.details || meta.triggerDetails || null,
      causedByParent: changedProps.length > 0,
      causedByState: changedState.length > 0,
      meta,
    };

    globalState.previousProps.set(componentName, nextSnapshot.props || {});
    globalState.previousStates.set(componentName, nextSnapshot.state || {});

    console.groupCollapsed(
      `[RenderProfile] ${componentName} #${renderCount} (${durationMs}ms)`
    );
    console.log(payload);
    console.groupEnd();
  };
};

export const profileCallback = async (name, meta, fn) => {
  if (!__DEV__) return fn();
  const startedAt = safeNow();
  markRenderTrigger(name, meta);
  try {
    return await fn();
  } finally {
    const durationMs = Number((safeNow() - startedAt).toFixed(2));
    console.log(`[PerfTrace] ${name}`, { ...meta, durationMs });
  }
};

export const profileSync = (name, meta, fn) => {
  if (!__DEV__) return fn();
  const startedAt = safeNow();
  markRenderTrigger(name, meta);
  try {
    return fn();
  } finally {
    const durationMs = Number((safeNow() - startedAt).toFixed(2));
    console.log(`[PerfTrace] ${name}`, { ...meta, durationMs });
  }
};
